import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import {
  generateOrganizationSchema,
  generateWebsiteSchema,
  generateBreadcrumbSchema,
} from '../utils/structuredData';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  article?: boolean;
  noindex?: boolean;
  canonical?: string;
  structuredData?: object | object[];
  alternateLanguages?: Array<{ hreflang: string; href: string }>;
  breadcrumbs?: Array<{ name: string; url: string }>;
  category?: string;
  tags?: string[];
  readingTime?: number;
  wordCount?: number;
}

const SEO: React.FC<SEOProps> = ({
  title = "Regal Outsource (India) Pvt. Ltd. - Leading BPO & AI Solutions Provider",
  description = "Transform your business with Regal Outsource's comprehensive BPO services, AI solutions, revenue cycle management, and software development. Serving 100+ global clients since 2012 with ISO-certified quality.",
  keywords = "business process outsourcing, BPO services India, AI solutions, revenue cycle management, healthcare BPO, software development, data processing, customer support outsourcing, offshore outsourcing, digital transformation, automation services",
  image = "https://regaloutsourceindia.com/assets/og-image.jpg",
  url = "https://regaloutsourceindia.com",
  type = "website",
  author = "Regal Outsource (India) Pvt. Ltd.",
  publishedTime,
  modifiedTime,
  article = false,
  noindex = false,
  canonical,
  structuredData,
  alternateLanguages,
  breadcrumbs,
  category,
  tags,
  readingTime,
  wordCount
}) => {
  const siteTitle = "Regal Outsource (India) Pvt. Ltd.";
  const fullTitle = title.includes(siteTitle) ? title : `${title} | ${siteTitle}`;
  
  const { pathname, search } = useLocation();
  const siteUrl = "https://regaloutsourceindia.com";

  // Normalize pathname to remove trailing slash, unless it's the root path
  const normalizedPathname = pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;

  const canonicalUrl = `${siteUrl}${normalizedPathname}${search}`;

  const currentUrl = canonical || canonicalUrl;
  const currentDate = new Date().toISOString();

  // Structured data generation
  const organizationSchema = generateOrganizationSchema();
  const websiteSchema = generateWebsiteSchema();
  const breadcrumbSchema = breadcrumbs ? generateBreadcrumbSchema(breadcrumbs) : null;

  const allStructuredData = [
    organizationSchema,
    websiteSchema,
    ...(structuredData ? (Array.isArray(structuredData) ? structuredData : [structuredData]) : []),
    ...(breadcrumbSchema ? [breadcrumbSchema] : [])
  ].filter(Boolean);


  // Enhanced keywords with location and industry terms
  const enhancedKeywords = `${keywords}, Mumbai BPO company, Maharashtra outsourcing, India IT services, business automation, process optimization, cost reduction, scalable solutions, 24/7 support, GDPR compliant, HIPAA compliant, ISO certified`;

  // Default alternate languages
  const defaultAlternateLanguages = [
    { hreflang: 'en-IN', href: currentUrl },
    { hreflang: 'x-default', href: currentUrl }
  ];

  const mergedAlternateLanguages = alternateLanguages || defaultAlternateLanguages;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={enhancedKeywords} />
      <meta name="author" content={author} />
      <meta name="robots" content={noindex ? "noindex,nofollow,noarchive,nosnippet" : "index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1"} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta httpEquiv="Content-Language" content="en-IN" />
      <meta name="revisit-after" content="3 days" />
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="referrer" content="origin-when-cross-origin" />

      {/* Enhanced SEO Meta Tags */}
      <meta name="geo.region" content="IN-MH" />
      <meta name="geo.placename" content="Mumbai, Maharashtra, India" />
      <meta name="geo.position" content="19.0760;72.8777" />
      <meta name="ICBM" content="19.0760, 72.8777" />
      <meta name="DC.title" content={fullTitle} />
      <meta name="DC.creator" content={author} />
      <meta name="DC.subject" content="Business Process Outsourcing, AI Solutions" />
      <meta name="DC.description" content={description} />
      <meta name="DC.publisher" content={siteTitle} />
      <meta name="DC.contributor" content={siteTitle} />
      <meta name="DC.date" content={currentDate} />
      <meta name="DC.type" content="Text" />
      <meta name="DC.format" content="text/html" />
      <meta name="DC.identifier" content={currentUrl} />
      <meta name="DC.language" content="en" />
      <meta name="DC.coverage" content="Worldwide" />
      <meta name="DC.rights" content="Copyright (c) 2024 Regal Outsource (India) Pvt. Ltd." />

      {/* Business/Industry Specific Meta Tags */}
      <meta name="industry" content="Business Process Outsourcing" />
      <meta name="coverage" content="Worldwide" />
      <meta name="target" content="all" />
      <meta name="HandheldFriendly" content="True" />
      <meta name="MobileOptimized" content="320" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="format-detection" content="telephone=yes" />
      <meta name="format-detection" content="address=yes" />

      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />

      {/* Alternate Language Links */}
      {mergedAlternateLanguages.map((lang, index) => (
        <link key={index} rel="alternate" hrefLang={lang.hreflang} href={lang.href} />
      ))}

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${siteTitle} - Business Process Outsourcing Services`} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={siteTitle} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="en_IN" />
      <meta property="business:contact_data:street_address" content="Udaipur, Rajasthan" />
      <meta property="business:contact_data:locality" content="Udaipur" />
      <meta property="business:contact_data:region" content="Rajasthan" />
      <meta property="business:contact_data:postal_code" content="313003" />
      <meta property="business:contact_data:country_name" content="India" />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={`${siteTitle} - BPO Services`} />
      <meta name="twitter:site" content="@regaloutsource" />
      <meta name="twitter:creator" content="@regaloutsource" />
      <meta name="twitter:domain" content="regaloutsourceindia.com" />

      {/* LinkedIn Meta Tags */}
      <meta property="linkedin:owner" content="regal-outsource-india" />

      {/* Article specific meta tags */}
      {article && (
        <>
          <meta property="article:author" content={author} />
          <meta property="article:publisher" content="https://www.facebook.com/RegalOutSource" />
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {category && <meta property="article:section" content={category} />}
          {tags && tags.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
          {readingTime && <meta name="twitter:label1" content="Reading time" />}
          {readingTime && <meta name="twitter:data1" content={`${readingTime} min read`} />}
          {wordCount && <meta name="twitter:label2" content="Word count" />}
          {wordCount && <meta name="twitter:data2" content={`${wordCount} words`} />}
        </>
      )}

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(allStructuredData)}
      </script>

      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#1e40af" />
      <meta name="msapplication-TileColor" content="#1e40af" />
      <meta name="application-name" content={siteTitle} />
      <meta name="apple-mobile-web-app-title" content={siteTitle} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      {/* Favicon and Icons */}
      <link rel="icon" type="image/x-icon" href="/favicon.ico" />
      <link rel="apple-touch-icon" href="/assets/og-image.jpg" />
      <link rel="manifest" href="/manifest.json" />

      {/* Preconnect to external domains for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://images.unsplash.com" />
      <link rel="preconnect" href="https://www.google-analytics.com" />
      <link rel="preconnect" href="https://www.googletagmanager.com" />
      <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
      <link rel="dns-prefetch" href="https://fonts.gstatic.com" />
      <link rel="dns-prefetch" href="https://images.unsplash.com" />

      {/* Preload critical resources */}
      <link rel="preload" href="/fonts/Kaggish-Bold.ttf" as="font" type="font/ttf" crossOrigin="anonymous" />
      <link rel="preload" href="/assets/og-image.jpg" as="image" />

      {/* Security Headers */}
      <meta httpEquiv="X-Content-Type-Options" content="nosniff" />
      <meta httpEquiv="X-XSS-Protection" content="1; mode=block" />
      <meta httpEquiv="Referrer-Policy" content="strict-origin-when-cross-origin" />
      <meta httpEquiv="Permissions-Policy" content="camera=(), microphone=(), geolocation=()" />
    </Helmet>
  );
};

export default SEO;
